<template>
  <div class="registration">
    <div class="container">
      <div
        class="column is-8 is-offset-2 is-12-touch is-offset-0-touch is-10-desktop is-offset-1-desktop"
      >
        <div v-if="!success" class="box">
          <br />
          <h1 class="title is-spaced has-text-centered">
            Har du en opgave, du vil have løst?
          </h1>

          <p class="subtitle has-text-centered is-6">
            Registrér dig som virksomhed, og kom igang med det samme! Det er
            ganske uforpligtende.
          </p>

          <br />

          <p class="title is-4 has-text-weight-light section-header">
            Personlige oplysninger
          </p>
          <p class="subtitle help">Påkrævet</p>

          <div class="columns">
            <div class="column">
              <b-field
                :label="$t('global.forms.firstName')"
                :type="{ 'is-danger': veeErrors.has('first_name') }"
                :message="veeErrors.first('first_name')"
              >
                <b-input
                  v-model="first_name"
                  name="first_name"
                  v-validate="'required'"
                  :data-vv-as="$t('global.forms.firstName')"
                />
              </b-field>
            </div>

            <div class="column">
              <b-field
                :label="$t('global.forms.lastName')"
                :type="{ 'is-danger': veeErrors.has('last_name') }"
                :message="veeErrors.first('last_name')"
              >
                <b-input
                  v-model="last_name"
                  name="last_name"
                  v-validate="'required'"
                  :data-vv-as="$t('global.forms.lastName')"
                />
              </b-field>
            </div>
          </div>

          <div class="columns">
            <div class="column">
              <b-field
                :label="$t('global.forms.phoneNumber')"
                :type="{ 'is-danger': veeErrors.has('phoneNumber') }"
                :message="veeErrors.first('phoneNumber')"
              >
                <b-input
                  v-model="phone_number"
                  type="phone"
                  name="phoneNumber"
                  v-validate="'required|min:8'"
                  :data-vv-as="$t('global.forms.phoneNumber')"
                />
              </b-field>
            </div>

            <div class="column">
              <b-field
                :label="$t('global.forms.email')"
                :type="{ 'is-danger': veeErrors.has('email') }"
                :message="veeErrors.first('email')"
              >
                <b-input
                  v-model="email"
                  type="email"
                  name="email"
                  v-validate="'required|email'"
                  :data-vv-as="$t('global.forms.email')"
                />
              </b-field>
            </div>
          </div>

          <div class="columns">
            <div class="column is-6">
              <b-field
                :label="$t('global.forms.password')"
                :type="{ 'is-danger': veeErrors.has('password') }"
                :message="veeErrors.first('password')"
              >
                <b-input
                  v-model="password"
                  type="password"
                  name="password"
                  password-reveal
                  v-validate="'required|alpha_dash|min:8'"
                  :data-vv-as="$t('global.forms.password')"
                />
              </b-field>
            </div>
          </div>

          <br />
          <p class="title is-4 has-text-weight-light is-spaced section-header">
            Virksomhed
          </p>

          <p class="subtitle is-6">
            Hvis du har virksomhed med CVR nummer, kan du indtaste det her. Så
            kan vi automatisk udfylde virsomhedens navn og adresse for dig.
          </p>

          <div class="columns">
            <div class="column">
              <div class="field">
                <label class="label" for="vat_number">{{
                  $t('global.forms.company.vat')
                }}</label>

                <p
                  class="control"
                  :class="{ 'is-loading': isLoadingVatNumber }"
                >
                  <input
                    type="text"
                    v-model="vat_number"
                    v-validate="'min:2|max:13'"
                    :data-vv-as="$t('global.forms.company.vat')"
                    :class="{ 'is-danger': veeErrors.has('vat_number') }"
                    autocomplete="off"
                    name="vat_number"
                    id="vat_number"
                    class="input"
                  />
                </p>

                <p v-if="veeErrors.has('vat_number')" class="help is-danger">
                  {{ veeErrors.first('vat_number') }}
                </p>
              </div>
            </div>

            <div class="column">
              <div class="field">
                <label class="label" for="companyName">{{
                  $t('global.forms.company.name')
                }}</label>

                <p class="control">
                  <input
                    type="text"
                    v-model="company_name"
                    class="input"
                    readonly
                    id="companyName"
                    placeholder="Hvis dit CVR-nummer findes, kan du se det matchende firmanavn her"
                  />
                </p>
              </div>
            </div>
          </div>

          <notification v-if="unknownVat" type="info">
            CVR-nummeret kunne ikke bekræftes. Du kan stadig fortsætte, vi
            forsøger at verificere på et senere tidspunkt.
          </notification>

          <br />
          <p class="title is-4 has-text-weight-light section-header">Adresse</p>

          <div class="columns">
            <div class="column">
              <b-field
                :label="$t('global.forms.postcode')"
                :type="{ 'is-danger': veeErrors.has('postcode') }"
                :message="veeErrors.first('postcode')"
              >
                <b-input
                  v-model="postcode"
                  type="text"
                  name="postcode"
                  v-validate="'required|min:4'"
                  :data-vv-as="$t('global.forms.postcode')"
                />
              </b-field>
            </div>

            <div class="column">
              <b-field
                :label="$t('global.forms.city')"
                :type="{ 'is-danger': veeErrors.has('city') }"
                :message="veeErrors.first('city')"
              >
                <b-input
                  v-model="city"
                  type="text"
                  name="city"
                  v-validate="'required'"
                  :data-vv-as="$t('global.forms.city')"
                />
              </b-field>
            </div>
          </div>

          <b-field
            :label="$t('global.forms.address')"
            :type="{ 'is-danger': veeErrors.has('address') }"
            :message="veeErrors.first('address')"
          >
            <b-input
              v-model="address"
              type="text"
              name="address"
              v-validate="'required'"
              :data-vv-as="$t('global.forms.address')"
            />
          </b-field>

          <br />

          <div class="columns">
            <div class="column">
              <b-field label="Hvor har du hørt om os fra?">
                <b-select v-model="referral" placeholder="Vælg et sted">
                  <option value="friend">Fra en ven/bekendt</option>
                  <option value="startup_central">Fra Startup Central</option>
                  <option value="google">Via Google-søgning</option>
                  <option value="facebook">Facebook</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="article">Artikel</option>
                  <option value="other">Andet</option>
                </b-select>
              </b-field>

              <b-field v-if="referral === 'other'" label="Andet:">
                <b-input
                  v-model="referralOther"
                  type="text"
                  name="andet"
                  v-validate="'required'"
                  data-vv-as="andet"
                />
              </b-field>
            </div>

            <div class="column">
              <b-checkbox v-model="newsletter">
                Ønsker du at modtage vores nyhedsbrev?
              </b-checkbox>
            </div>
          </div>

          <br />

          <!-- Controls -->
          <div class="buttons is-right">
            <router-link :to="{ name: 'home' }" class="button" exact>
              {{ $t('global.actions.back') }}
            </router-link>

            <b-button
              type="is-primary"
              @click="submit"
              :loading="isSubmitting"
              :disabled="veeErrors.any()"
            >
              {{ $t('global.actions.create') }}
            </b-button>
          </div>
        </div>

        <div v-else class="box">
          <figure class="image is-128x128 logo">
            <img src="/static/logos/logo-no-text.png" alt="taskflex.dk" />
          </figure>

          <h1 class="title is-spaced has-text-centered">
            VELKOMMEN TIL TASKFLEX
          </h1>

          <h3
            class="subtitle has-text-grey has-text-weight-light has-text-centered"
          >
            Vi har registeret din bruger!
          </h3>

          <h4>Hvad sker der nu?</h4>

          <p>
            Du er meget tæt på at komme i gang med at skrive til de studerende!
            Du skal blot lige bekræfte din email.
          </p>

          <br />

          <h4>Bekræft din email</h4>

          <p>Vi har sendt en bekræftelses-email til:</p>
          <p class="email-container has-text-centered has-text-primary">
            {{ email }}
          </p>

          <br />

          <!-- Controls -->
          <div class="buttons is-centered">
            <router-link :to="{ name: 'home' }">
              <div class="button is-primary">
                <span>Til forsiden</span>
              </div>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '../../../global/styles/variables';

.registration {
  background: url('/static/backgrounds/white-bricks-people.jpeg') no-repeat
    center bottom;
  background-size: cover;

  .section-header {
    padding-bottom: 2px;
    border-bottom: 2px solid $primary;
  }

  .logo {
    margin: 0 auto 2rem;
  }
}
</style>

<script>
/* eslint-disable camelcase */
import CustomerService from '@/taskflex/customers/services/CustomerService'
import VatService from '@/taskflex/customers/services/VatService'
import Notification from '@/global/components/Notification'

export default {
  data () {
    return {
      first_name: null,
      last_name: null,
      email: null,
      password: null,
      phone_number: null,

      company_name: null,
      vat_number: null,

      address: null,
      postcode: null,
      city: null,

      referral: null,
      referralOther: null,

      isLoadingVatNumber: false,
      unknownVat: false,

      success: false,
      isSubmitting: false,
      newsletter: false
    }
  },

  watch: {
    vat_number (vatNumber) {
      this.unknownVat = false

      // TODO: debounce?.
      if (vatNumber.length >= 2 && vatNumber.length <= 13) {
        // Wait for typing to finish, or confirm via button
        this.checkVat()
      }
    }
  },

  methods: {
    checkVat () {
      this.isLoadingVatNumber = true
      this.company_name = ''

      const options = {
        vatNumber: this.vat_number
      }

      VatService.check(
        options,
        ({ data: { data } }) => {
          this.company_name = data.name

          this.address = data.address.address
          this.postcode = data.address.postcode.toString()
          this.city = data.address.city

          this.isLoadingVatNumber = false
        },
        () => {
          this.isLoadingVatNumber = false
          this.unknownVat = true
        }
      )
    },

    submit () {
      this.$validator.validateAll().then(result => {
        if (!result) {
          return
        }

        const {
          first_name,
          last_name,
          email,
          phone_number,
          address,
          postcode,
          city,
          password,
          newsletter
        } = this

        const data = {
          first_name,
          last_name,
          email,
          phone_number,
          address,
          postcode,
          city,
          password,
          newsletter
        }

        if (this.vat_number || this.company_name) {
          data.vat_number = this.vat_number
          data.company_name = this.company_name
        }

        if (this.referral) {
          data.referral = this.referral

          if (this.referral === 'other') {
            data.referral = `other: ${this.referralOther}`
          }
        }

        this.isSubmitting = true

        CustomerService.create(
          data,
          () => {
            this.success = true
            this.isSubmitting = false
          },
          ({ response }) => {
            const { errors } = response.data
            this.isSubmitting = false

            // TODO: make into mixin - ApiService.parseValidationErrors() ?
            Object.entries(errors).forEach(([key, values]) => {
              values.forEach(value => {
                if (value === 'validation.unique') {
                  this.veeErrors.add({
                    field: key,
                    msg: 'Denne email er allerede registeret i vores system.' // TODO: Translate
                  })
                }
              })
            })
          }
        )
      })
    }
  },

  components: {
    Notification
  }
}
</script>



// WEBPACK FOOTER //
// src/taskflex/customers/pages/Register.vue